// Keyboard shortcut help overlay — Theme K, task K2.
//
// Opened by the `?` hotkey. Reuses Modal.tsx for focus-trap, Esc-close, and
// body-scroll-lock so we get all that behaviour for free.
//
// Navigation rows are GENERATED from `hotkeyMap()` (lib/navigation.ts, the
// single navigation source) instead of a hand-maintained list — otherwise
// this overlay silently drifts from the real g-chords the moment they change
// (as happened before: the code moved on, this list didn't).

import { Modal } from "./Modal";
import { Keyboard } from "lucide-preact";
import { hotkeyMap } from "../lib/navigation";
import { uxEnabled } from "../lib/ux";

interface HotkeyHelpProps {
  onClose: () => void;
}

interface ShortcutRow {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  heading: string;
  rows: ShortcutRow[];
}

/**
 * One row per NavTarget's PRIMARY hotkey (aliases like the old `g m` for
 * "Aufgaben" are intentionally not shown a second time — they're muscle-
 * memory compatibility, not the documented shortcut).
 */
function navigationRows(): ShortcutRow[] {
  return Object.entries(hotkeyMap())
    .filter(([key, target]) => key === target.hotkey)
    .map(([key, target]) => ({ keys: ["g", key], description: target.label }));
}

function buildGroups(): ShortcutGroup[] {
  return [
    {
      heading: "Navigation",
      rows: navigationRows(),
    },
    {
      heading: "Aktionen",
      rows: [
        { keys: ["c"], description: "Erstellen" },
        // UX-Vorschlag P2 — nur mit Flag, sonst bleibt die Liste wie bisher.
        ...(uxEnabled("ux_web_p2") ? [{ keys: ["n"], description: "Erfassen (Schnellerfassung)" }] : []),
        { keys: ["/"], description: "Suche öffnen" },
        { keys: ["?"], description: "Shortcuts anzeigen" },
        { keys: ["⌘", "K"], description: "Command-Palette" },
      ],
    },
    // UX-Vorschlag P6 — Tastatur in der Projekttabelle, nur mit Flag.
    ...(uxEnabled("ux_web_p6")
      ? [
          {
            heading: "Projekttabelle",
            rows: [
              { keys: ["j", "k"], description: "Nächste / vorige Zeile (auch ↓ / ↑)" },
              { keys: ["x"], description: "Zeile markieren" },
              { keys: ["↵"], description: "Aufgabe öffnen" },
              { keys: ["e"], description: "Umbenennen" },
              { keys: ["s"], description: "Status weiterschalten" },
              { keys: ["p"], description: "Priorität weiterschalten" },
              { keys: ["a"], description: "Sub-Aufgabe anlegen" },
            ],
          },
        ]
      : []),
  ];
}

function Kbd({ label }: { label: string }): preact.JSX.Element {
  return (
    <kbd class="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] text-micro font-mono text-[color:var(--color-on-surface-variant)] shadow-sm">
      {label}
    </kbd>
  );
}

export function HotkeyHelp({ onClose }: HotkeyHelpProps): preact.JSX.Element {
  const groups = buildGroups();
  return (
    <Modal title="Tastatur-Shortcuts" onClose={onClose} size="sm">
      <div class="space-y-5">
        {groups.map((group) => (
          <section key={group.heading}>
            <h3 class="text-micro font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-2 flex items-center gap-1.5">
              <Keyboard size={14} aria-hidden="true" />
              {group.heading}
            </h3>
            <ul class="space-y-1">
              {group.rows.map((row) => (
                <li
                  key={row.description}
                  class="flex items-center justify-between gap-4 py-1"
                >
                  <span class="text-sm text-[color:var(--color-on-surface)]">
                    {row.description}
                  </span>
                  <span class="flex items-center gap-1 shrink-0">
                    {row.keys.map((k, i) => (
                      <span key={i} class="flex items-center gap-1">
                        {i > 0 && (
                          <span class="text-label text-[color:var(--color-on-surface-variant)]">
                            then
                          </span>
                        )}
                        <Kbd label={k} />
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  );
}
